import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, Stagger, Item } from "@/components/shared/Reveal";

const FOUNDED = 2013;
const YEARS = new Date().getFullYear() - FOUNDED;

const PHASES = [
  { label: "Security & surveillance", text: "Founded in Dubai as a security and surveillance systems company, building the discipline for regulated, mission-critical installations." },
  { label: "Vehicle CCTV & tracking", text: "Extended into mobile video, MDVR and GPS tracking for buses, trucks and passenger fleets across the UAE." },
  { label: "IoT fleet & logistics", text: "Grew into telematics, sensors, fleet platforms and logistics automation — connecting vehicles to operations." },
  { label: "Integrated transportation technology", text: "Today: one systems integrator across hardware, software, connectivity and AI for the entire transportation operation." },
];

const Counter = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 1400);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, to]);
  return <span ref={ref} data-testid="heritage-years-counter">{n}</span>;
};

export const Heritage = () => (
  <section data-testid="heritage-section" className="section relative overflow-hidden border-y border-line bg-navy-900/60">
    <div className="pointer-events-none absolute -left-24 top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-cyan-400/5 blur-3xl" />
    <div className="wrap grid gap-14 lg:grid-cols-12 lg:items-center">
      <Reveal className="lg:col-span-5">
        <div className="eyebrow">Established {FOUNDED} · Dubai</div>
        <div className="mt-6 flex items-end gap-3">
          <span className="font-heading text-[7rem] font-bold leading-none tracking-tighter text-white sm:text-[9rem]"><Counter to={YEARS} /></span>
          <span className="mb-4 font-heading text-4xl font-semibold text-cyan-400">+</span>
        </div>
        <div className="mt-2 font-heading text-2xl font-semibold text-white sm:text-3xl">years live in transportation technology.</div>
        <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400 md:text-lg">
          Wexoz has operated continuously in Dubai since {FOUNDED} — through every generation of vehicle CCTV, GPS, telematics, IoT and AI. That longevity is what allows us to take responsibility for an entire technology layer, not just a product.
        </p>
        <div className="mt-8 grid grid-cols-3 gap-3">
          {[["Since " + FOUNDED, "Dubai-based"], ["Regulated", "SIRA · SecurePath · TDRA"], ["Multi-vendor", "Technology agnostic"]].map(([a, b]) => (
            <div key={a} className="rounded-xl border border-line bg-navy/60 p-4">
              <div className="font-heading text-sm font-semibold text-white">{a}</div>
              <div className="mt-1 text-xs text-slate-500">{b}</div>
            </div>
          ))}
        </div>
      </Reveal>

      <div className="lg:col-span-7">
        <SectionHeading eyebrow="How we evolved" title="From security installer to transportation systems integrator." />
        <Stagger className="relative mt-10 grid gap-6 sm:grid-cols-2" stagger={0.1}>
          {PHASES.map((p, i) => (
            <Item key={p.label} data-testid={`heritage-phase-${i + 1}`} className="card relative p-7">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-cyan-400">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-px flex-1 bg-line" />
                {i === PHASES.length - 1 && <span className="rounded-full bg-cyan-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-cyan-300">Today</span>}
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-white">{p.label}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.text}</p>
            </Item>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);
